import { Stack } from "@mui/material";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import { observer } from "mobx-react";
import React from "react";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import { useStore } from "utils/useStore";
import AppliedFilterChip from "../BaseChipComponents/AppliedFilterChipBase";
import { IAppliedFilterChipComponent } from "../BaseChipComponents/IAppliedFilterChipComponent";
import {
    CHIP_AGGREGATION_THRESHOLD,
    renderCollectionChips,
    useChipFiltersStore,
} from "../BaseChipComponents/chipUtils";

export const AgentManagerChips = observer(function (
    props: IAppliedFilterChipComponent,
) {
    const applicationFiltersStore = useChipFiltersStore(props);
    const applicationFilterValuesStore = useStore(ApplicationFilterValuesStore);

    const isAgentManagerLoading = applicationFilterValuesStore.getTaskLoading(
        ApplicationFilterValuesStore.Tasks.LOAD_AGENT_MANAGERS,
    );

    const agentManagerIds = applicationFiltersStore.agentManagerIds;
    const agentManagerChipCount = agentManagerIds.length;
    const shouldGroupAgentManagers =
        agentManagerChipCount > CHIP_AGGREGATION_THRESHOLD;
    const getAgentManagerLabel = (agentManagerId: string) => {
        const manager = applicationFilterValuesStore.agentManagers.find(
            (agentManager) => agentManager.id === agentManagerId,
        );

        return (
            manager?.fullName?.trim() ||
            "Unknown Agent Manager"
        );
    };

    const agentManagerChipLoadingIndicator = (
        <Stack
            direction="row"
            flexWrap="nowrap"
            alignItems="center"
            spacing={1}
        >
            <span>Agent Manager:</span>
            <AcxLoadingIndicator size={16} alternate="PuffLoader" />
        </Stack>
    );

    if (isAgentManagerLoading) {
        if (shouldGroupAgentManagers) {
            return (
                <AppliedFilterChip
                    disabled={props.disabled}
                    isApplicable={props.isApplicable}
                >
                    {agentManagerChipLoadingIndicator}
                </AppliedFilterChip>
            );
        }
        return agentManagerIds.map((agentManagerId) => (
            <AppliedFilterChip
                disabled={props.disabled}
                isApplicable={props.isApplicable}
                key={agentManagerId}
            >
                {agentManagerChipLoadingIndicator}
            </AppliedFilterChip>
        ));
    }

    return renderCollectionChips({
        items: agentManagerIds,
        disabled: props.disabled,
        isApplicable: props.isApplicable,
        getKey: (agentManagerId) => agentManagerId,
        getLabel: (agentManagerId) =>
            `Agent Manager: ${getAgentManagerLabel(agentManagerId)}`,
        getTooltipLabel: getAgentManagerLabel,
        getAggregatedLabel: (count) => `${count} Agent Managers`,
        onRemoveAll: () => applicationFiltersStore.setAgentManagerIds([]),
        onRemoveItem: (agentManagerId) =>
            applicationFiltersStore.setAgentManagerIds(
                applicationFiltersStore.agentManagerIds.filter(
                    (selectedAgentManagerId) =>
                        selectedAgentManagerId !== agentManagerId,
                ),
            ),
    });
});
